import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  ButtonIcon,
  ButtonText,
  OutlineCard,
  TagInput
} from '@morressier/alembic';
import { FormFieldStringInputArray as IFormFieldStringInputArray } from '@publishing-engine/domain/types/article';
import { FieldError, useFormContext } from 'react-hook-form';
import { Box, Flex, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { FieldLabel } from 'src/components/Field';
import { get, isEqual } from 'lodash';
import { toSentenceCase } from 'src/utils/string';
import { hasDuplicateKeywords } from 'src/utils/hasDuplicateKeywords';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function FormFieldStringInputArray({
  config,
  isMetadataExtracted,
  editKeywords
}: {
  config: IFormFieldStringInputArray;
  isMetadataExtracted: boolean;
  editKeywords: string;
}) {
  const form = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;
  const error = get(form.formState.errors, field);
  const { submitCount } = form.formState;
  const rootField = field.substring(field.lastIndexOf('.') + 1, field.length);
  const values = form.watch(rootField as any);
  const [isEditing, setIsEditing] = useState(!values || values.length === 0);
  const tagInputWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (values && Array.isArray(values)) {
      const nextValues = values.map((v: { text: string }) => v.text);
      const currentValues = form.getValues(field as any) || [];

      if (!isEqual(currentValues, nextValues)) {
        form.setValue(field as any, nextValues, {
          shouldValidate: submitCount > 0
        });
      }
    }
  }, [field, form, submitCount, values]);

  useEffect(() => {
    if (submitCount === 0 || !Array.isArray(values)) return;

    if (hasDuplicateKeywords(values)) {
      form.setError(field as any, {
        type: 'manual',
        message: 'Duplicate values are not allowed'
      });
      return;
    }

    if ((error as FieldError)?.type === 'manual') {
      form.clearErrors(field as any);
    }
  }, [error, field, form, submitCount, values]);

  const ClearAll = () => {
    form.setValue(rootField as any, [], { shouldValidate: submitCount > 0 });
  };

  const getArrayFieldErrorMessage = (fieldLabel?: string) => {
    if (!error) return undefined;

    if ((error as FieldError)?.message) {
      return (error as FieldError).message?.includes('must NOT have fewer than')
        ? `A minimum of ${validations.minCountOfKeywords} ${toSentenceCase(
            fieldLabel || ''
          )} is required`
        : (error as FieldError).message;
    }

    if (Array.isArray(error)) {
      const firstError = error.find(Boolean);
      if (firstError?.message?.includes('must NOT have more than')) {
        return `Each ${toSentenceCase(fieldLabel || '')} must be ${
          validations.maxLength
        } characters or fewer`;
      }
      return firstError?.message;
    }

    return undefined;
  };

  const shouldShowError = submitCount > 0 && Boolean(error);

  const helperText = useBreakpointValue<
    (validations?: {
      required?: boolean;
      maxCountOfKeywords?: number;
      maxLength?: number;
      minCountOfKeywords?: number;
      minLength?: number;
    }) => string
  >({
    base: validations =>
      `Tap + to add  ${toSentenceCase(rootField)}${
        validations?.minCountOfKeywords !== undefined
          ? `. (Minimum ${validations.minCountOfKeywords} required)`
          : ''
      }`,
    md: validations =>
      `Press Enter to add  ${toSentenceCase(rootField)}${
        validations?.minCountOfKeywords !== undefined
          ? `. (Minimum ${validations.minCountOfKeywords} required)`
          : ''
      }`
  });

  const placeholder = useBreakpointValue({
    base: 'Type and tap + to add',
    md: 'Type and press Enter to add'
  });

  const handleAddKeyword = () => {
    const input = tagInputWrapperRef.current?.querySelector('input');
    const value = input?.value?.trim();
    if (input && value) {
      const currentValues = form.getValues(rootField as any) || [];
      form.setValue(rootField as any, [...currentValues, { text: value }], {
        shouldValidate: submitCount > 0
      });
      input.value = '';
    }
  };

  const renderReadView = () => (
    <OutlineCard width="100%" data-cy="keywords-read-view" padding={4}>
      <Flex flexDirection="column" gap={4}>
        <Text textStyle="headingItem" data-cy="keywords-heading">
          Keywords
        </Text>
        <Flex gap={1} data-cy="keywords-list" wrap="wrap">
          {values.map((keyword: { text: string }) => (
            <Badge
              size="context"
              key={keyword.text}
              data-cy="keyword-badge"
              whiteSpace="pre-wrap"
            >
              {keyword.text}
            </Badge>
          ))}
        </Flex>
        <ButtonText
          width="fit-content"
          variant="primary"
          onClick={() => setIsEditing(true)}
          data-cy="edit-keywords-button"
        >
          {editKeywords}
        </ButtonText>
      </Flex>
    </OutlineCard>
  );

  const renderEditView = () => (
    <>
      <OutlineCard width="100%" padding={4}>
        <Flex flexDirection="column" gap={4} data-cy="keywords-edit-view">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
            data-cy="keywords-field-label"
          />
          <Flex gap={2} alignItems="center" ref={tagInputWrapperRef}>
            <Box flex={1}>
              <TagInput
                isInvalid={shouldShowError}
                control={form.control}
                name={rootField}
                placeholder={placeholder}
                helperText={helperText?.(validations)}
                errorMessage={
                  shouldShowError
                    ? getArrayFieldErrorMessage(rootField)
                    : undefined
                }
                data-cy="keywords-tag-input"
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  '& span': {
                    whiteSpace: 'normal !important',
                    wordBreak: 'break-word'
                  }
                }}
              />
            </Box>
            <Box display={{ base: 'block', md: 'none' }}>
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label="Add"
                borderRadius={4}
              >
                <ButtonIcon
                  size="small"
                  ariaLabel="add-button"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  data-cy="tag-input-add-button"
                  onClick={handleAddKeyword}
                />
              </Tooltip>
            </Box>
          </Flex>
        </Flex>
        <Box marginTop="4px">
          <ButtonText onClick={ClearAll}>Clear All</ButtonText>
        </Box>
      </OutlineCard>
    </>
  );

  return isMetadataExtracted && !isEditing
    ? renderReadView()
    : renderEditView();
}
